/**
 * Mobile League Home Page
 * Shows: Banner, Standings Preview, Message Board, Box Scores
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { dynastyTheme } from '../../../services/colorService';
import { mobileTileStyle } from '../../../services/mobileStyleService';
import { leaguesAPI } from '../../../services/apiService';
import { fetchBoxScore, fetchAvailableDates } from '../../../services/boxScoreApi';
import MobileBoxScoreTile from '../components/MobileBoxScoreTile';
import MobileStandingsPreview from '../components/MobileStandingsPreview';
import MobileLeagueNavigation from '../components/MobileLeagueNavigation';
import MobileLeagueStandings from './MobileLeagueStandings';
import MobileLeagueTransactions from './MobileLeagueTransactions';
import MobileLeagueMessages from './MobileLeagueMessages';
import MobileFreeAgentSearch from './MobileFreeAgentSearch';
import MobileLiveScoring from './MobileLiveScoring';
import MobileLeagueTradingBlock from './MobileLeagueTradingBlock';
import MobileTeamBoxScore from './MobileTeamBoxScore';
import MobilePageHeader from '../components/MobilePageHeader';
import MobileSectionHeader from '../components/MobileSectionHeader';
import MobileTeamLogo from '../components/MobileTeamLogo';

const C = dynastyTheme.tokens.colors;
const SECTION_BORDER = `1px solid ${C.border}`;

// Section wrapper: 14px horizontal inset (per spec) + an inter-section separator
// (border-top + 6px margin) for the 2nd/3rd. The LABEL inside is the canonical
// MobileSectionHeader (9px symmetric buffer, 12px/600) — NOT a local copy, so it
// can't drift from the rest of the app.
const SectionWrap = ({ divider, children }) => (
  <div style={{ padding: '0 14px', ...(divider ? { marginTop: 6, borderTop: SECTION_BORDER } : null) }}>
    {children}
  </div>
);

const ChatIcon = ({ size = 11 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 5h14a1 1 0 011 1v8a1 1 0 01-1 1H9l-4 3V6a1 1 0 011-1z" /></svg>
);
const SendIcon = ({ size = 11 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4z" /></svg>
);

const formatTimeAgo = (ts) => {
  if (!ts) return '';
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (isNaN(diff)) return '';
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};
const fmtShort = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Flush message-board preview row (mock treatment): gold avatar · owner · time ·
// reply count · 2-line body · Reply. Border-top separator.
const MessageRow = ({ msg, onReply }) => (
  <div style={{ display: 'flex', gap: 9, padding: '9px 14px', borderTop: `1px solid ${C.hairline}` }}>
    {/* Square team picture (shared MobileTeamLogo), not a gold-initials circle. */}
    <MobileTeamLogo name={msg.owner_name || msg.team_name} logoUrl={msg.team_logo_url} size={28} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 7, minWidth: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.textHi, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{msg.owner_name || 'Unknown'}</span>
          <span style={{ fontSize: 9, color: C.muted, whiteSpace: 'nowrap', flexShrink: 0 }}>{formatTimeAgo(msg.created_at)}</span>
        </span>
        {msg.reply_count > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: C.muted, flexShrink: 0 }}><ChatIcon />{msg.reply_count}</span>}
      </div>
      <div style={{ fontSize: 13, color: C.coolGray[150], lineHeight: 1.4, marginTop: 1, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{msg.message_text}</div>
      <div onClick={onReply} role="button" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 5, fontSize: 11, color: C.muted, cursor: 'pointer' }}><ChatIcon />Reply</div>
    </div>
  </div>
);

function MobileLeagueHome({ leagueId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [league, setLeague] = useState(null);
  const [standings, setStandings] = useState(null);
  const [standingsThroughDate, setStandingsThroughDate] = useState(null);
  const [teams, setTeams] = useState([]);
  const [boxScores, setBoxScores] = useState({});
  const [boxScoresLoading, setBoxScoresLoading] = useState(false);
  const [yesterdayDate, setYesterdayDate] = useState(null);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState('home');
  const [boxScoreTeamId, setBoxScoreTeamId] = useState(null);
  const [userTeamId, setUserTeamId] = useState(null);
  const [userTeamInfo, setUserTeamInfo] = useState(null);

  // League Messages preview (mock treatment) — reuse the message data layer.
  const [messages, setMessages] = useState([]);
  const [messageStats, setMessageStats] = useState({ total: 0, unread: 0 });
  const [messagesOpen, setMessagesOpen] = useState(false);  // semi-collapsible list

  // Swipe detection state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const [initialGameId, setInitialGameId] = useState(null);

  // Support deep-linking from Quick Actions via location state
  useEffect(() => {
    if (location.state?.initialPage) {
      setCurrentPage(location.state.initialPage);
    }
    if (location.state?.initialGameId) {
      setInitialGameId(location.state.initialGameId);
    }
  }, [location.state]);

  const handleNavigate = (pageId) => {
    setCurrentPage(pageId);
    console.log('Navigating to:', pageId);
  };

  // Get page display name
  const getPageName = () => {
    const pageNames = {
      'home': 'Dashboard',
      'standings': 'Standings',
      'messages': 'Messages',
      'transactions': 'Transactions',
      'live-scoring': 'Live Scoring',
      'trading-block': 'Trading Block',
      'free-agents': 'Free Agents'
    };
    return pageNames[currentPage] || 'Dashboard';
  };

  useEffect(() => {
    if (leagueId) {
      loadLeagueData();
    }
  }, [leagueId]);

  useEffect(() => {
    if (teams.length > 0 && leagueId) {
      fetchAllBoxScores();
    }
  }, [teams, leagueId]);

  // League Messages preview data (last 5 + counts).
  useEffect(() => {
    if (!leagueId) return;
    leaguesAPI.getMessages(leagueId, 5, 0, true)
      .then((res) => { if (res?.success) setMessages(res.messages || []); })
      .catch(() => {});
    leaguesAPI.getMessageStats(leagueId)
      .then((res) => {
        const s = res?.stats || res || {};
        setMessageStats({ total: s.total_messages ?? 0, unread: s.unread_messages ?? 0 });
      })
      .catch(() => {});
  }, [leagueId]);

  const loadLeagueData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Parallel fetch: league details, standings, and user's team
      const [leagueRes, standingsRes, myTeamRes] = await Promise.all([
        leaguesAPI.getLeagueDetails(leagueId),
        leaguesAPI.getLeagueStandings(leagueId),
        leaguesAPI.getUserTeam(leagueId).catch(() => null) // Gracefully handle if no team
      ]);

      if (leagueRes.success && leagueRes.league) {
        setLeague(leagueRes.league);
      }

      if (standingsRes.success) {
        setStandings({
          categories: standingsRes.categories,
          teams: standingsRes.standings || standingsRes.teams || []
        });
        setTeams(standingsRes.teams || standingsRes.standings || []);
        if (standingsRes.through_date) setStandingsThroughDate(standingsRes.through_date);
      }
      
      // Set user's team info for highlighting and header display
      if (myTeamRes && myTeamRes.team_id) {
        setUserTeamId(myTeamRes.team_id);
        setUserTeamInfo(myTeamRes);
      }
    } catch (err) {
      console.error('Error loading league data:', err);
      setError('Failed to load league data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBoxScores = async () => {
    setBoxScoresLoading(true);

    try {
      const firstTeam = teams[0];
      if (!firstTeam) {
        setBoxScoresLoading(false);
        return;
      }

      // Get most recent game date
      const datesResponse = await fetchAvailableDates(leagueId, firstTeam.team_id);

      if (!datesResponse.success || !datesResponse.dates || datesResponse.dates.length === 0) {
        setBoxScoresLoading(false);
        return;
      }

      const sortedDates = [...datesResponse.dates].sort((a, b) => b.localeCompare(a));
      const mostRecentDate = sortedDates[0];
      setYesterdayDate(mostRecentDate);

      // Fetch box scores in batches
      const BATCH_SIZE = 3;
      const boxScoresMap = {};

      for (let i = 0; i < teams.length; i += BATCH_SIZE) {
        const batch = teams.slice(i, i + BATCH_SIZE);

        const batchPromises = batch.map(team =>
          fetchBoxScore(leagueId, team.team_id, mostRecentDate)
            .then(data => ({ teamId: team.team_id, data }))
            .catch(err => ({ teamId: team.team_id, error: err }))
        );

        const batchResults = await Promise.all(batchPromises);

        batchResults.forEach(result => {
          if (result.error) {
            boxScoresMap[result.teamId] = { error: result.error };
          } else {
            boxScoresMap[result.teamId] = result.data;
          }
        });

        if (i + BATCH_SIZE < teams.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      setBoxScores(boxScoresMap);
    } catch (error) {
      console.error('Error fetching box scores:', error);
    } finally {
      setBoxScoresLoading(false);
    }
  };

  const handleViewFullStandings = () => {
    // Navigate to standings tab in mobile
    setCurrentPage('standings');
  };

  const handleViewAllMessages = () => {
    // Navigate to messages tab in mobile
    setCurrentPage('messages');
  };

  const handleViewBoxScore = (teamId) => {
    setBoxScoreTeamId(teamId || null);
    setCurrentPage('box-scores');
  };
  
  // Swipe handlers - minimum 50px swipe to trigger
  const minSwipeDistance = 50;
  
  const onTouchStart = (e) => {
    setTouchEnd(null); // Reset
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    // Swipe LEFT (right to left) = navigate to team home
    if (isLeftSwipe && currentPage === 'home' && userTeamId) {
      console.log('🏠 Swiping left to team home');
      navigate(`/leagues/${leagueId}/team`);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          padding: '20px',
          paddingBottom: '0',
            backgroundColor: 'transparent'
        }}
      >
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-neutral-800 rounded"></div>
          <div className="h-64 bg-neutral-800 rounded"></div>
          <div className="h-48 bg-neutral-800 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: '20px',
          paddingBottom: '0',
            backgroundColor: 'transparent'
        }}
      >
        <div className={`p-4 rounded bg-red-500/20 text-red-400 flex items-center gap-2`}>
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  // Sort teams by standings for box scores
  const sortedTeams = [...teams].sort((a, b) => {
    const aPoints = a.totalPoints || a.total_points || 0;
    const bPoints = b.totalPoints || b.total_points || 0;
    return bPoints - aPoints;
  });

  return (
    <div
      style={{
        paddingBottom: '0',
        backgroundColor: 'transparent',
        position: 'relative'
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* League Navigation Bar */}
      <MobileLeagueNavigation currentPage={currentPage} onNavigate={handleNavigate} />

      {/* Swipe Indicator - ONLY show on home page when user has a team */}
      {currentPage === 'home' && userTeamId && (
        <div
          style={{
            position: 'fixed',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            opacity: 0.6,
            pointerEvents: 'none'
          }}
        >
          <div
            style={{
              fontSize: '10px',
              fontWeight: '600',
              color: dynastyTheme.tokens.colors.primary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              writingMode: 'vertical-rl',
              textOrientation: 'mixed'
            }}
          >
            Team
          </div>
          <div
            style={{
              fontSize: '20px',
              color: dynastyTheme.tokens.colors.primary
            }}
          >
            ‹
          </div>
        </div>
      )}

      {/* League Header. Live Scoring uses the LOCKED nav-system structure: nav →
          4px → static header-tile.jpg banner (edge-to-edge, untinted, NO page-name
          overlay, NO league-banner tint) → 4px. Other league pages keep
          MobilePageHeader until each is redesigned to the locked spec. */}
      {(currentPage === 'live-scoring' || currentPage === 'home') ? (
        // home: NO bottom margin — the first section header owns the 9px gap to its
        // label, so banner-bottom→STANDINGS = exactly the header's 9px (not 4+9=13).
        <div style={{ marginTop: '4px', marginBottom: currentPage === 'home' ? '0px' : '4px' }}>
          <img src="/header-tile.jpg" alt="League banner" style={{ width: '100%', display: 'block' }} />
        </div>
      ) : (
        <MobilePageHeader
          title={league?.league_name || 'Loading...'}
          subtitle={userTeamInfo ? `${userTeamInfo.team_name || ''}${userTeamInfo.manager_name ? ` · ${userTeamInfo.manager_name}` : ''}` : null}
          pageBadge={getPageName()}
          bannerUrl={league?.league_settings?.league_banner_url}
          showLogo={false}
        />
      )}

      {/* Conditional Page Content */}
      {currentPage === 'home' ? (
        <>
          <style>{`.lh-xscroll::-webkit-scrollbar{display:none;}.lh-xscroll{scrollbar-width:none;}.lh-noscroll::-webkit-scrollbar{display:none;}`}</style>

          {/* ===== Standings (reuse MobileStandingsPreview) ===== */}
          <SectionWrap>
            <MobileSectionHeader
              title="Standings"
              right={<>
                {standingsThroughDate && <span style={{ fontSize: 10, color: C.muted, whiteSpace: 'nowrap' }}>Through {fmtShort(standingsThroughDate)}</span>}
                <span onClick={handleViewFullStandings} role="button" style={{ fontSize: 11, fontWeight: 700, color: C.primary, whiteSpace: 'nowrap', cursor: 'pointer' }}>View All →</span>
              </>}
            />
          </SectionWrap>
          {standings
            ? <MobileStandingsPreview standings={standings} throughDate={standingsThroughDate} onViewFull={handleViewFullStandings} userTeamId={userTeamId} leagueId={leagueId} />
            : <div style={{ textAlign: 'center', padding: 24, color: C.muted, fontSize: 12 }}>Loading standings…</div>}

          {/* ===== League Messages (semi-collapsible flush rows) ===== */}
          <SectionWrap divider>
            <MobileSectionHeader
              title="League Messages"
              chevron={messages.length > 0 ? { expanded: messagesOpen, onClick: () => setMessagesOpen((v) => !v) } : undefined}
              right={<>
                <span style={{ fontSize: 10, color: C.muted, whiteSpace: 'nowrap' }}>{messageStats.total} · <span style={{ color: C.primary, fontWeight: 700 }}>{messageStats.unread} unread</span></span>
                <button onClick={handleViewAllMessages} style={{ display: 'flex', alignItems: 'center', gap: 4, background: C.primary, color: C.neutral[950], border: 'none', borderRadius: 6, padding: '5px 9px', fontSize: 10, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}><SendIcon />Post</button>
              </>}
            />
          </SectionWrap>
          {messages.length === 0
            ? <div style={{ padding: '12px 14px', color: C.muted, fontSize: 12, borderTop: `1px solid ${C.hairline}` }}>No messages yet</div>
            : (
              /* Semi-collapsible scroll window (same pattern as Player Notes / Probables):
                 collapsed shows the first few, chevron grows it; all messages scroll within. */
              <div className="lh-noscroll" style={{ maxHeight: messagesOpen ? 380 : 188, overflowY: 'auto', transition: 'max-height .2s', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {messages.map((m) => <MessageRow key={m.message_id} msg={m} onReply={handleViewAllMessages} />)}
              </div>
            )}
          <div style={{ padding: '9px 14px', borderTop: `1px solid ${C.hairline}` }}>
            <span onClick={handleViewAllMessages} role="button" style={{ fontSize: 12, fontWeight: 700, color: C.primary, cursor: 'pointer' }}>View Full Message Board →</span>
          </div>

          {/* ===== Yesterday's Box Scores (horizontal snap slider, reuse MobileBoxScoreTile) ===== */}
          <SectionWrap divider>
            <MobileSectionHeader
              title="Yesterday's Box Scores"
              right={yesterdayDate ? <span style={{ fontSize: 9, fontWeight: 700, color: C.primary, whiteSpace: 'nowrap' }}>{fmtShort(yesterdayDate)} · swipe →</span> : null}
            />
          </SectionWrap>
          <div
            className="lh-xscroll"
            style={{ overflowX: 'auto', scrollSnapType: 'x proximity' }}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', gap: 10, padding: '4px 14px 12px', width: 'max-content' }}>
              {boxScoresLoading
                ? [0, 1, 2].map((i) => <div key={i} style={{ width: 286, height: 320, flexShrink: 0, background: C.ground.screen }} />)
                : sortedTeams.length === 0
                  ? <div style={{ padding: 24, color: C.muted, fontSize: 12 }}>No teams in league yet</div>
                  : sortedTeams.map((team) => (
                    <div key={team.team_id} style={{ flexShrink: 0, scrollSnapAlign: 'start' }}>
                      <MobileBoxScoreTile
                        teamId={team.team_id}
                        teamName={team.team_name}
                        teamLogoUrl={team.team_logo_url}
                        boxScoreData={boxScores[team.team_id]}
                        loading={boxScoresLoading}
                        error={boxScores[team.team_id]?.error}
                        date={yesterdayDate}
                        onViewFull={handleViewBoxScore}
                        leagueId={leagueId}
                        scoringCategories={league?.league_settings?.scoring_categories}
                      />
                    </div>
                  ))}
            </div>
          </div>
        </>
      ) : currentPage === 'standings' ? (
        /* Full Standings Page */
        <MobileLeagueStandings
          league={league}
          teams={teams}
          leagueId={leagueId}
          userTeam={{ team_id: userTeamId }}
        />
      ) : currentPage === 'transactions' ? (
        /* Transaction Log Page */
        <MobileLeagueTransactions
          league={league}
          leagueId={leagueId}
        />
      ) : currentPage === 'messages' ? (
        /* Message Board Page */
        <MobileLeagueMessages
          league={league}
          leagueId={leagueId}
          user={null}
          userTeam={{ team_id: userTeamId }}
        />
      ) : currentPage === 'free-agents' ? (
        /* Free Agent Search Page */
        <MobileFreeAgentSearch
          leagueId={leagueId}
          league={league}
          user={null}
        />
      ) : currentPage === 'live-scoring' ? (
        /* Live Scoring Page */
        <MobileLiveScoring leagueId={leagueId} initialGameId={initialGameId} />
      ) : currentPage === 'box-scores' ? (
        /* Full Box Score Page */
        <MobileTeamBoxScore leagueId={leagueId} initialTeamId={boxScoreTeamId} />
      ) : currentPage === 'trading-block' ? (
        /* Trading Block Page */
        <MobileLeagueTradingBlock leagueId={leagueId} currentUserTeamId={userTeamId} />
      ) : (
        /* Placeholder pages for other sections */
        <div className="px-4 py-8">
          <div style={{ ...mobileTileStyle, padding: '32px', textAlign: 'center' }}>
            <div className="text-5xl mb-4">
              {currentPage === 'standings' && '🏆'}
              {currentPage === 'messages' && '💬'}
              {currentPage === 'transactions' && '📋'}
              {currentPage === 'live-scoring' && '⚡'}
            </div>
            <h2 className={`text-xl font-bold ${dynastyTheme.classes.text.white} mb-2`}>
              {getPageName()}
            </h2>
            <p className={`${dynastyTheme.classes.text.neutralLight} mb-4`}>
              Coming Soon
            </p>
            <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
              This section is under development
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default MobileLeagueHome;
